import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Home,
  Edit,
  Save,
  Cancel,
  Visibility,
  VisibilityOff,
  CreditCard,
  Loyalty,
  Star,
  CalendarMonth,
  AttachMoney,
} from '@mui/icons-material';

export default function CustomerProfile() {
  const [editMode, setEditMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [profile, setProfile] = useState({
    firstName: 'Emily',
    lastName: 'Johnson',
    email: 'emily.johnson@gmail.com',
    phone: '+1 (555) 234-5678',
    address: '456 Oak Avenue, Los Angeles, CA 90001',
    idType: 'Passport',
    idNumber: 'P12345678',
    dateOfBirth: '1990-05-15',
    nationality: 'United States',
    memberSince: '2023-06-15',
    loyaltyPoints: 2500,
    totalBookings: 12,
    totalSpent: 4200,
  });

  const [tempProfile, setTempProfile] = useState(profile);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleEditToggle = () => {
    if (editMode) {
      setTempProfile(profile);
    }
    setEditMode(!editMode);
  };

  const handleSave = () => {
    setProfile(tempProfile);
    setEditMode(false);
    setSnackbar({
      open: true,
      message: 'Profile updated successfully!',
      severity: 'success',
    });
  };

  const handlePasswordChange = () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setSnackbar({
        open: true,
        message: 'Passwords do not match!',
        severity: 'error',
      });
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setSnackbar({
        open: true,
        message: 'Password must be at least 6 characters!',
        severity: 'error',
      });
      return;
    }
    setSnackbar({
      open: true,
      message: 'Password changed successfully!',
      severity: 'success',
    });
    setPasswordDialogOpen(false);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  const stats = [
    {
      label: 'Total Bookings',
      value: profile.totalBookings,
      icon: CalendarMonth,
      color: '#1976d2',
    },
    {
      label: 'Loyalty Points',
      value: profile.loyaltyPoints,
      icon: Star,
      color: '#ffa726',
    },
    {
      label: 'Total Spent',
      value: `$${profile.totalSpent}`,
      icon: AttachMoney,
      color: '#2e7d32',
    },
    {
      label: 'Member Since',
      value: new Date(profile.memberSince).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
      icon: Loyalty,
      color: '#9c27b0',
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            My Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your account information and preferences
          </Typography>
        </Box>
        {!editMode ? (
          <Button variant="contained" startIcon={<Edit />} onClick={handleEditToggle}>
            Edit Profile
          </Button>
        ) : (
          <Box display="flex" gap={1}>
            <Button variant="outlined" startIcon={<Cancel />} onClick={handleEditToggle}>
              Cancel
            </Button>
            <Button variant="contained" startIcon={<Save />} onClick={handleSave}>
              Save Changes
            </Button>
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 120,
                height: 120,
                margin: '0 auto',
                bgcolor: '#1976d2',
                fontSize: '3rem',
                mb: 2,
              }}
            >
              {profile.firstName[0]}{profile.lastName[0]}
            </Avatar>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {profile.firstName} {profile.lastName}
            </Typography>
            <Chip
              label="Premium Member"
              color="primary"
              icon={<Star />}
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary" mb={1}>
              {profile.email}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {profile.phone}
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setPasswordDialogOpen(true)}
            >
              Change Password
            </Button>
          </Paper>

          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Account Statistics
            </Typography>
            <Grid container spacing={2}>
              {stats.map((stat) => {
                const IconComponent = stat.icon;
                return (
                  <Grid item xs={6} key={stat.label}>
                    <Box textAlign="center" p={2} sx={{ bgcolor: `${stat.color}10`, borderRadius: 2 }}>
                      <Avatar sx={{ bgcolor: stat.color, width: 40, height: 40, margin: '0 auto', mb: 1 }}>
                        <IconComponent fontSize="small" />
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold" color={stat.color}>
                        {stat.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Personal Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={editMode ? tempProfile.firstName : profile.firstName}
                  onChange={(e) => setTempProfile({ ...tempProfile, firstName: e.target.value })}
                  disabled={!editMode}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={editMode ? tempProfile.lastName : profile.lastName}
                  onChange={(e) => setTempProfile({ ...tempProfile, lastName: e.target.value })}
                  disabled={!editMode}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editMode ? tempProfile.email : profile.email}
                  onChange={(e) => setTempProfile({ ...tempProfile, email: e.target.value })}
                  disabled={!editMode}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={editMode ? tempProfile.phone : profile.phone}
                  onChange={(e) => setTempProfile({ ...tempProfile, phone: e.target.value })}
                  disabled={!editMode}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={editMode ? tempProfile.address : profile.address}
                  onChange={(e) => setTempProfile({ ...tempProfile, address: e.target.value })}
                  disabled={!editMode}
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Home />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={editMode ? tempProfile.dateOfBirth : profile.dateOfBirth}
                  onChange={(e) => setTempProfile({ ...tempProfile, dateOfBirth: e.target.value })}
                  disabled={!editMode}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nationality"
                  value={editMode ? tempProfile.nationality : profile.nationality}
                  onChange={(e) => setTempProfile({ ...tempProfile, nationality: e.target.value })}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" fontWeight="bold" gutterBottom mt={4}>
              Identification
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="ID Type"
                  value={editMode ? tempProfile.idType : profile.idType}
                  onChange={(e) => setTempProfile({ ...tempProfile, idType: e.target.value })}
                  disabled={!editMode}
                  SelectProps={{ native: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CreditCard />
                      </InputAdornment>
                    ),
                  }}
                >
                  <option value="Passport">Passport</option>
                  <option value="Driver's License">Driver's License</option>
                  <option value="National ID">National ID</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ID Number"
                  value={editMode ? tempProfile.idNumber : profile.idNumber}
                  onChange={(e) => setTempProfile({ ...tempProfile, idNumber: e.target.value })}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" fontWeight="bold" gutterBottom mt={4}>
              Loyalty Program
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Card sx={{ bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight="bold">
                    Premium Member
                  </Typography>
                  <Chip label="Active" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                </Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  {profile.loyaltyPoints} Points
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Member since {new Date(profile.memberSince).toLocaleDateString()}
                </Typography>
                <Box mt={2} p={2} sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    Your Benefits:
                  </Typography>
                  <Typography variant="caption">
                    • 10% discount on all services
                  </Typography>
                  <br />
                  <Typography variant="caption">
                    • Free room upgrades (subject to availability)
                  </Typography>
                  <br />
                  <Typography variant="caption">
                    • Late checkout privileges
                  </Typography>
                  <br />
                  <Typography variant="caption">
                    • Priority customer support
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Current Password"
            type={showPassword ? 'text' : 'password'}
            value={passwordForm.currentPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            value={passwordForm.newPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
            margin="normal"
            helperText="Password must be at least 6 characters"
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type={showPassword ? 'text' : 'password'}
            value={passwordForm.confirmPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePasswordChange}>
            Change Password
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
